import axios, { AxiosInstance } from "axios";
import { getUserAuth } from "../helpers";

const getCleanBaseURL = (envKey: string = "REACT_APP_SERVER_URL") => {
  const url = process.env[envKey];
  if (!url) return "";
  // Strip any leading/trailing spaces and surrounding single or double quotes
  return url.trim().replace(/^['"]|['"]$/g, "").trim();
};

const cleanBaseUrl = getCleanBaseURL("REACT_APP_SERVER_URL");
const cleanB2BBaseUrl = getCleanBaseURL("REACT_APP_B2B_SERVER_URL");

console.log("========================================");
console.log("API Env Raw URL:", process.env["REACT_APP_SERVER_URL"]);
console.log("API Resolved Base URL:", cleanBaseUrl);
console.log("B2B API Env Raw URL:", process.env["REACT_APP_B2B_SERVER_URL"]);
console.log("B2B API Resolved Base URL:", cleanB2BBaseUrl);
console.log("========================================");

export const api = axios.create({
  baseURL: cleanBaseUrl,
});

export const b2bApi = axios.create({
  baseURL: cleanB2BBaseUrl,
  headers: {
    "activation-key": process.env.REACT_APP_ACTIVATION_KEY || "",
    // "activation_key": "pjoiv4qwo0iivp1kau4w2gzeq1gfzpy1zhghnkxxq8kcx2rbzyyyc4kfkmrg",
  },
});

const logoutUser = () => {
  // Perform any necessary cleanup tasks, e.g., clearing local storage
  localStorage.clear();
  // Redirect to the login page or perform any other required action
  window.alert(
    "Attention: Your session has expired. Please log in again to continue accessing the system. Thank you!"
  );
  window.location.href = "/auth/login"; // Replace with your desired logout destination
};

const pendingRequests = new Map<string, AbortController>();

export const abortAllPendingRequests = () => {
  pendingRequests.forEach((controller) => {
    controller.abort();
  });
  pendingRequests.clear();
};

const setupInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use((config) => {
    const fullUrl = `${config.baseURL || ""}${config.url || ""}`;
    console.log(`[API Request Initiated] ${config.method?.toUpperCase()} ${fullUrl}`);
    const accessToken = getUserAuth();
    if (accessToken && config.headers) {
      config.headers["auth-Token"] = `${accessToken}`;
    }

    // Generate a key based on method and URL path (excluding query params)
    const urlPath = config.url?.split("?")[0];
    if (urlPath) {
      const key = `${config.method?.toLowerCase()}:${urlPath}`;

      // If there is an existing pending request for this key, abort it
      if (pendingRequests.has(key)) {
        const existingController = pendingRequests.get(key);
        existingController?.abort();
        pendingRequests.delete(key);
      }

      const controller = new AbortController();

      // If the caller has provided their own signal, listen to it and abort our controller when they do
      if (config.signal) {
        if (config.signal.aborted) {
          controller.abort();
        } else {
          config.signal.addEventListener("abort", () => {
            controller.abort();
          });
        }
      }

      config.signal = controller.signal;
      pendingRequests.set(key, controller);
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Remove completed request from pending map
      if (response.config?.url) {
        const urlPath = response.config.url.split("?")[0];
        const key = `${response.config.method?.toLowerCase()}:${urlPath}`;
        pendingRequests.delete(key);
      }
      return response;
    },
    (error) => {
      // Remove failed/cancelled request from pending map
      if (error.config?.url) {
        const urlPath = error.config.url.split("?")[0];
        const key = `${error.config.method?.toLowerCase()}:${urlPath}`;
        pendingRequests.delete(key);
      }

      if (axios.isCancel(error)) {
        // Return a pending promise to silently stop execution of downstream then/catch blocks
        return new Promise(() => { });
      }

      if (error.response && [401].includes(error.response.status)) {
        logoutUser();
      }

      // Ensure error.response and error.response.data exist to prevent crashes 
      // in callers (like handleLogin in AuthProvider.tsx) that access error.response.data.msg
      if (!error.response) {
        error.response = {
          status: 500,
          statusText: "Internal Error",
          headers: {},
          config: error.config || {},
          data: {
            msg: error.message || "A network or CORS error occurred. Please verify your backend server connection."
          }
        };
      } else if (!error.response.data) {
        error.response.data = {
          msg: "An unexpected server error occurred."
        };
      } else if (typeof error.response.data === "string") {
        error.response.data = {
          msg: error.response.data
        };
      } else if (!error.response.data.msg) {
        error.response.data.msg = error.response.data.message || "An unexpected server error occurred.";
      }

      return Promise.reject(error);
    }
  );
};

setupInterceptors(api);
setupInterceptors(b2bApi);

// API function to create call manual
export const createCallManual = async (payload: {
  mobile_no: string;
  email: string;
  name: string;
  address: string;
  package: string;
  disposition: string;
  source: string;
  description: string;
  comment: string;
  status: string;
}) => {
  try {
    const response = await api.post('/api-create-call-manual', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating call manual:', error);
    throw error;
  }
};